import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CalendarRange, Save, Plug, CheckCircle2, XCircle, Loader2, RefreshCw, UserPlus, UserCog, UserX, AlertTriangle, FileSpreadsheet, Mail, TrendingUp } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function Settings({ user, onLogout }) {
  const [form, setForm] = useState({ salary_period_start_day: 26, salary_period_end_day: 25, public_base_url: '' });
  const [computedPeriod, setComputedPeriod] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payday, setPayday] = useState({ environment: 'sandbox', client_id: '', has_secret: false, status: null, company_name: null, last_test: null });
  const [paydaySecret, setPaydaySecret] = useState('');
  const [paydaySaving, setPaydaySaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [email, setEmail] = useState({ method: 'smtp', mail_from: '', smtp_username: '', has_smtp_password: false, graph_tenant_id: '', graph_client_id: '', has_graph_secret: false });
  const [smtpPassword, setSmtpPassword] = useState('');
  const [graphSecret, setGraphSecret] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [increase, setIncrease] = useState(null);
  const [loadingIncrease, setLoadingIncrease] = useState(false);
  const [applyingIncrease, setApplyingIncrease] = useState(false);

  const fetchSyncLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payday/sync-log`, authHeaders());
      setSyncLog(res.data || []);
    } catch { /* ekkert */ }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [settingsRes, paydayRes, emailRes] = await Promise.all([
          axios.get(`${API}/settings`, authHeaders()),
          axios.get(`${API}/payday/settings`, authHeaders()),
          axios.get(`${API}/email/settings`, authHeaders()),
        ]);
        setForm({
          salary_period_start_day: settingsRes.data?.salary_period_start_day
            || (settingsRes.data?.salary_period_start ? parseInt(settingsRes.data.salary_period_start.split('-')[2], 10) : 26),
          salary_period_end_day: settingsRes.data?.salary_period_end_day
            || (settingsRes.data?.salary_period_end ? parseInt(settingsRes.data.salary_period_end.split('-')[2], 10) : 25),
          public_base_url: settingsRes.data?.public_base_url || '',
        });
        if (settingsRes.data?.salary_period_start && settingsRes.data?.salary_period_end) {
          setComputedPeriod({ from: settingsRes.data.salary_period_start, to: settingsRes.data.salary_period_end });
        }
        setPayday(paydayRes.data);
        setMapping(paydayRes.data.timesheet_mapping || []);
        setValidation(paydayRes.data.mapping_validation || null);
        setEmail(emailRes.data);
      } catch {
        toast.error('Villa við að sækja stillingar');
      }
    };
    fetchAll();
    fetchSyncLog();
  }, [fetchSyncLog]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, authHeaders());
      const res = await axios.get(`${API}/settings`, authHeaders());
      if (res.data?.salary_period_start && res.data?.salary_period_end) {
        setComputedPeriod({ from: res.data.salary_period_start, to: res.data.salary_period_end });
      }
      toast.success('Stillingar vistaðar!');
    } catch {
      toast.error('Villa við að vista stillingar');
    } finally {
      setSaving(false);
    }
  };

  const handlePaydaySave = async (e) => {
    e.preventDefault();
    setPaydaySaving(true);
    try {
      const payload = { environment: payday.environment, client_id: payday.client_id };
      if (paydaySecret) payload.client_secret = paydaySecret;
      const res = await axios.put(`${API}/payday/settings`, payload, authHeaders());
      setPayday(res.data);
      setPaydaySecret('');
      toast.success('Payday stillingar vistaðar!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Villa við að vista Payday stillingar');
    } finally {
      setPaydaySaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/payday/test-connection`, {}, authHeaders());
      setPayday((prev) => ({ ...prev, status: 'connected', company_name: res.data.company_name }));
      toast.success(`Tenging við Payday virkar!${res.data.company_name ? ` (${res.data.company_name})` : ''}`);
    } catch (err) {
      setPayday((prev) => ({ ...prev, status: 'error' }));
      toast.error(err.response?.data?.detail || 'Tenging við Payday mistókst');
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/payday/sync-employees`, {}, authHeaders());
      setSyncResult(res.data);
      const s = res.data.summary;
      toast.success(`Samstilling lokið: ${s.created} nýir, ${s.updated} uppfærðir, ${s.deactivated} óvirkir, ${s.errors} villur`);
      fetchSyncLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Samstilling mistókst');
    } finally {
      setSyncing(false);
    }
  };

  const handleMappingSave = async () => {
    setMappingSaving(true);
    try {
      await axios.put(`${API}/payday/settings`, { timesheet_mapping: mapping }, authHeaders());
      setValidation(null);
      toast.success('Launaliðir vistaðir!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Villa við að vista launaliði');
    } finally {
      setMappingSaving(false);
    }
  };

  const handleValidateMapping = async () => {
    setValidating(true);
    try {
      const res = await axios.post(`${API}/payday/validate-mapping`, {}, authHeaders());
      setValidation({ checked_at: new Date().toISOString(), missing: res.data.missing });
      if (res.data.missing.length === 0) {
        toast.success('Allir virkir launaliðir eru til í Payday!');
      } else {
        toast.error(`Þessir launaliðir eru EKKI til í Payday: ${res.data.missing.join(', ')}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Villa við að prófa launaliði');
    } finally {
      setValidating(false);
    }
  };

  const handleEmailSave = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      const payload = {
        method: email.method, mail_from: email.mail_from, smtp_username: email.smtp_username,
        graph_tenant_id: email.graph_tenant_id, graph_client_id: email.graph_client_id,
      };
      if (smtpPassword) payload.smtp_password = smtpPassword;
      if (graphSecret) payload.graph_client_secret = graphSecret;
      const res = await axios.put(`${API}/email/settings`, payload, authHeaders());
      setEmail(res.data);
      setSmtpPassword('');
      setGraphSecret('');
      toast.success('Póststillingar vistaðar!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Villa við að vista póststillingar');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleEmailTest = async () => {
    if (!testTo) { toast.error('Sláðu inn netfang til að prófa'); return; }
    setTestingEmail(true);
    try {
      await axios.post(`${API}/email/test`, { to: testTo }, authHeaders());
      toast.success(`Prófunarpóstur sendur á ${testTo}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Póstsending mistókst');
    } finally {
      setTestingEmail(false);
    }
  };

  const actionLabel = (a) => ({ created: 'Nýskráður', updated: 'Uppfærður', deactivated: 'Gerður óvirkur', sent: 'Sent', error: 'Villa' }[a] || a);

  const handleIncreasePreview = async () => {
    setLoadingIncrease(true);
    try {
      const res = await axios.get(`${API}/wage-increase/2027/preview`, authHeaders());
      setIncrease(res.data);
    } catch {
      toast.error('Ekki tókst að sækja forskoðun');
    } finally { setLoadingIncrease(false); }
  };

  const handleIncreaseApply = async () => {
    if (!window.confirm(`Keyra launahækkun 2027 fyrir ${increase.pending_count} starfsmenn?\n\nLaun hækka um 3,5% eða að lágmarki 23.750 kr. á mánaðarlaun. Þetta er ekki hægt að afturkalla sjálfkrafa.`)) return;
    setApplyingIncrease(true);
    try {
      const res = await axios.post(`${API}/wage-increase/2027/apply`, {}, authHeaders());
      toast.success(`Launahækkun keyrð: ${res.data.updated} starfsmenn hækkaðir`);
      await handleIncreasePreview();
    } catch {
      toast.error('Villa við að keyra launahækkun');
    } finally { setApplyingIncrease(false); }
  };

  const fmtIsk = (v) => (v == null ? '–' : Math.round(v).toLocaleString('is-IS'));
  const actionIcon = (a) => {
    if (a === 'created') return <UserPlus className="h-4 w-4 text-green-600" />;
    if (a === 'updated') return <UserCog className="h-4 w-4 text-blue-600" />;
    if (a === 'deactivated') return <UserX className="h-4 w-4 text-gray-500" />;
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="settings-page">
        <h1 className="text-2xl font-bold">Stillingar</h1>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarRange className="h-5 w-5 text-blue-600" />
              Launatímabil
            </CardTitle>
            <p className="text-sm text-gray-500">
              Gildir fyrir alla starfsmenn. Veldu mánaðardaga (t.d. 26. til 25.) – tímabilið reiknast sjálfkrafa
              og rúllar áfram um mánaðamót. Notað sem sjálfgefið tímabil í tímaskrá og Excel-útflutningi.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                <div className="space-y-2">
                  <Label>Frá degi mánaðar</Label>
                  <select
                    value={form.salary_period_start_day}
                    onChange={(e) => setForm({ ...form, salary_period_start_day: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="settings-salary-period-start-day-select"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}.</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Til dags mánaðar</Label>
                  <select
                    value={form.salary_period_end_day}
                    onChange={(e) => setForm({ ...form, salary_period_end_day: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="settings-salary-period-end-day-select"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}.</option>
                    ))}
                  </select>
                </div>
              </div>
              {computedPeriod && (
                <p className="text-sm text-gray-600" data-testid="settings-computed-period">
                  Núverandi tímabil: <span className="font-medium">{computedPeriod.from}</span> til <span className="font-medium">{computedPeriod.to}</span>
                </p>
              )}
              <div className="space-y-2 max-w-lg">
                <Label>Opinber vefslóð (fyrir undirritunarhlekki)</Label>
                <Input
                  type="url"
                  value={form.public_base_url}
                  onChange={(e) => setForm({ ...form, public_base_url: e.target.value })}
                  placeholder="t.d. https://timar.abon.is"
                  data-testid="settings-public-url-input"
                />
                <p className="text-xs text-gray-500">
                  Undirritunarhlekkir í samningapóstum nota þessa slóð. Ef tómt er slóð vafrans notuð.
                  Lénið þarf að vísa á appið, annars fá starfsmenn dauða hlekki.
                </p>
              </div>
              <Button type="submit" disabled={saving} data-testid="settings-save-btn">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Vistar...' : 'Vista'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-lg">
              <span className="flex items-center gap-2">
                <Plug className="h-5 w-5 text-purple-600" />
                Payday samþætting
              </span>
              {payday.status === 'connected' && (
                <span className="flex items-center gap-1 text-sm font-normal text-green-600" data-testid="payday-status-connected">
                  <CheckCircle2 className="h-4 w-4" /> Tengt{payday.company_name ? ` – ${payday.company_name}` : ''}
                </span>
              )}
              {payday.status === 'error' && (
                <span className="flex items-center gap-1 text-sm font-normal text-red-600" data-testid="payday-status-error">
                  <XCircle className="h-4 w-4" /> Villa í tengingu
                </span>
              )}
              {!payday.status && (
                <span className="text-sm font-normal text-gray-400" data-testid="payday-status-none">Ótengt</span>
              )}
            </CardTitle>
            <p className="text-sm text-gray-500">
              Tenging við Payday launakerfið. Búðu til OAuth-forrit í stillingum fyrirtækisins í Payday til að fá Client ID og Client Secret.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePaydaySave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                <div className="space-y-2">
                  <Label>Umhverfi</Label>
                  <select
                    value={payday.environment}
                    onChange={(e) => setPayday({ ...payday, environment: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="payday-environment-select"
                  >
                    <option value="sandbox">Sandbox (prófunarumhverfi)</option>
                    <option value="production">Production (raunumhverfi)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input
                    value={payday.client_id}
                    onChange={(e) => setPayday({ ...payday, client_id: e.target.value })}
                    placeholder="Client ID frá Payday"
                    data-testid="payday-client-id-input"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Client Secret</Label>
                  <Input
                    type="password"
                    value={paydaySecret}
                    onChange={(e) => setPaydaySecret(e.target.value)}
                    placeholder={payday.has_secret ? '••••••••••••  (vistað – sláðu inn nýtt til að breyta)' : 'Client Secret frá Payday'}
                    data-testid="payday-client-secret-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={paydaySaving} data-testid="payday-save-btn">
                  <Save className="mr-2 h-4 w-4" />
                  {paydaySaving ? 'Vistar...' : 'Vista'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || !payday.client_id || (!payday.has_secret && !paydaySecret)}
                  data-testid="payday-test-connection-btn"
                >
                  {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />}
                  {testing ? 'Prófa...' : 'Prófa tengingu'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {payday.status === 'connected' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Samstilling starfsmanna
              </CardTitle>
              <p className="text-sm text-gray-500">
                Sendir alla starfsmenn héðan yfir í Payday. Nýir eru búnir til, breytingar uppfærðar og óvirkir starfsmenn
                gerðir óvirkir í Payday – engum er eytt.
                {payday.last_sync && ` Síðast samstillt: ${new Date(payday.last_sync).toLocaleString('is-IS')}.`}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleSync} disabled={syncing} data-testid="payday-sync-btn">
                {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {syncing ? 'Samstilli...' : 'Samstilla starfsmenn núna'}
              </Button>

              {syncResult && (
                <div className="border rounded-md divide-y" data-testid="payday-sync-result">
                  {syncResult.details.map((d, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        {actionIcon(d.action)}
                        {d.name}
                      </span>
                      <span className={d.action === 'error' ? 'text-red-600' : 'text-gray-500'}>
                        {actionLabel(d.action)}{d.detail ? ` – ${d.detail}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {syncLog.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Samstillingarsaga</h4>
                  <div className="border rounded-md divide-y">
                    {syncLog.map((log) => (
                      <div key={log.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`sync-log-${log.id}`}>
                        <span className="text-gray-600">
                          {new Date(log.timestamp).toLocaleString('is-IS')}
                          <span className="ml-2 text-xs text-gray-400">({log.environment}{log.type === 'timesheet' ? ' · tímaskrá' : log.type === 'auto' ? ' · sjálfvirk' : ''})</span>
                        </span>
                        <span className="text-gray-500">
                          {log.type === 'timesheet' ? (
                            <>
                              {log.summary.employees} starfsmenn · {log.summary.items} launaliðir ·{' '}
                              <span className={log.summary.errors > 0 ? 'text-red-600 font-medium' : ''}>{log.summary.errors} villur</span>
                            </>
                          ) : (
                            <>
                              {log.summary.created} nýir · {log.summary.updated} uppfærðir · {log.summary.deactivated} óvirkir ·{' '}
                              <span className={log.summary.errors > 0 ? 'text-red-600 font-medium' : ''}>{log.summary.errors} villur</span>
                            </>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {payday.status === 'connected' && mapping.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                Launaliðir í Payday
              </CardTitle>
              <p className="text-sm text-gray-500">
                Þegar tímaskrá er send í Payday („Senda í Payday" hnappur á Tímaskrá) þurfa nöfnin hér að vera
                nákvæmlega eins og launaliðirnir heita í Payday. Slökktu á liðum sem á ekki að senda.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {validation?.missing?.length > 0 && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700 max-w-2xl" data-testid="mapping-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Þessir launaliðir eru <strong>ekki til í Payday</strong>: {validation.missing.join(', ')}.
                    Tímaskrársending mun klikka – búðu þá til í Payday eða endurnefndu/slökktu á þeim hér.
                  </span>
                </div>
              )}
              <div className="border rounded-md divide-y max-w-2xl">
                {mapping.map((m, idx) => (
                  <div key={m.key} className="flex items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={m.enabled}
                      onChange={(e) => setMapping(mapping.map((x, i) => (i === idx ? { ...x, enabled: e.target.checked } : x)))}
                      className="h-4 w-4"
                      data-testid={`mapping-enabled-${m.key}`}
                    />
                    <span className={`w-56 text-sm ${m.enabled ? '' : 'text-gray-400'}`}>{m.label}</span>
                    <Input
                      value={m.payday_name}
                      onChange={(e) => setMapping(mapping.map((x, i) => (i === idx ? { ...x, payday_name: e.target.value } : x)))}
                      disabled={!m.enabled}
                      className="flex-1"
                      data-testid={`mapping-name-${m.key}`}
                    />
                    {m.enabled && validation && (
                      validation.missing?.includes((m.payday_name || '').trim()) ? (
                        <span className="flex items-center gap-1 text-xs text-red-600 whitespace-nowrap" data-testid={`mapping-status-missing-${m.key}`}>
                          <XCircle className="h-4 w-4" /> Ekki til
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-green-600 whitespace-nowrap" data-testid={`mapping-status-ok-${m.key}`}>
                          <CheckCircle2 className="h-4 w-4" /> Til
                        </span>
                      )
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleMappingSave} disabled={mappingSaving} data-testid="mapping-save-btn">
                  <Save className="mr-2 h-4 w-4" />
                  {mappingSaving ? 'Vistar...' : 'Vista launaliði'}
                </Button>
                <Button variant="outline" onClick={handleValidateMapping} disabled={validating} data-testid="mapping-validate-btn">
                  {validating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  {validating ? 'Prófa...' : 'Prófa launaliði í Payday'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Launahækkun 2027
            </CardTitle>
            <p className="text-sm text-gray-500">
              Samkvæmt SA/Efling kjarasamningi hækka laun 1. janúar 2027 um 3,5%, þó að lágmarki 23.750 kr. á mánaðarlaun.
              Starfsmenn á kauptöxtum fá nýja 2027-taxta, aðrir fá almenna hækkun. Skoðaðu forskoðun og staðfestu áður en hækkunin er keyrð.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleIncreasePreview} disabled={loadingIncrease} data-testid="wage-increase-preview-btn">
                {loadingIncrease ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {loadingIncrease ? 'Sæki...' : 'Sækja forskoðun'}
              </Button>
              {increase && increase.pending_count > 0 && (
                <Button onClick={handleIncreaseApply} disabled={applyingIncrease} className="bg-green-600 hover:bg-green-700" data-testid="wage-increase-apply-btn">
                  {applyingIncrease ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TrendingUp className="mr-2 h-4 w-4" />}
                  {applyingIncrease ? 'Keyri...' : `Keyra launahækkun (${increase.pending_count})`}
                </Button>
              )}
            </div>
            {increase && (
              <div className="space-y-2" data-testid="wage-increase-preview">
                <p className="text-sm text-gray-600">
                  {increase.pending_count} bíða hækkunar · {increase.applied_count} þegar hækkaðir eða á 2027-töxtum
                </p>
                {increase.rows.length === 0 ? (
                  <p className="text-sm text-gray-400">Engir virkir starfsmenn með skráð laun.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border rounded-md">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="px-3 py-2">Starfsmaður</th>
                          <th className="px-3 py-2">Aðferð</th>
                          <th className="px-3 py-2">Mánaðarlaun</th>
                          <th className="px-3 py-2">Dagvinna</th>
                          <th className="px-3 py-2">Yfirvinna</th>
                          <th className="px-3 py-2">Staða</th>
                        </tr>
                      </thead>
                      <tbody>
                        {increase.rows.map((r) => (
                          <tr key={r.employee_id} className="border-t" data-testid={`wage-increase-row-${r.employee_id}`}>
                            <td className="px-3 py-2 font-medium">{r.name}</td>
                            <td className="px-3 py-2">
                              {r.method === 'taxti'
                                ? `Taxti${r.launaflokkur ? ` (fl. ${r.launaflokkur})` : ''}`
                                : 'Almenn 3,5% / lágm. 23.750 kr.'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {fmtIsk(r.current.monthly_salary)}{!r.already_at_2027 && !r.already_applied && r.new.monthly_salary != null && <> → <span className="font-semibold text-green-700">{fmtIsk(r.new.monthly_salary)}</span></>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {fmtIsk(r.current.day_rate)}{!r.already_at_2027 && !r.already_applied && r.new.day_rate != null && <> → <span className="font-semibold text-green-700">{fmtIsk(r.new.day_rate)}</span></>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {fmtIsk(r.current.overtime_rate)}{!r.already_at_2027 && !r.already_applied && r.new.overtime_rate != null && <> → <span className="font-semibold text-green-700">{fmtIsk(r.new.overtime_rate)}</span></>}
                            </td>
                            <td className="px-3 py-2">
                              {r.already_applied ? (
                                <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="h-4 w-4" /> Hækkað{r.applied_at ? ` ${r.applied_at.slice(0, 10)}` : ''}</span>
                              ) : r.already_at_2027 ? (
                                <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="h-4 w-4" /> Á 2027-töxtum</span>
                              ) : (
                                <span className="text-amber-600 text-xs">Bíður</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-orange-600" />
              Tölvupóstur (Microsoft 365)
            </CardTitle>
            <p className="text-sm text-gray-500">
              Notað til að senda ráðningarsamninga til rafrænnar undirritunar. Veldu SMTP (netfang + lykilorð)
              eða Graph API (Azure app skráning).
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label>Aðferð</Label>
                  <select
                    value={email.method}
                    onChange={(e) => setEmail({ ...email, method: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="email-method-select"
                  >
                    <option value="smtp">SMTP (netfang + lykilorð)</option>
                    <option value="graph">Microsoft Graph API</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Sendanda-netfang (From)</Label>
                  <Input value={email.mail_from} onChange={(e) => setEmail({ ...email, mail_from: e.target.value })}
                    placeholder="t.d. laun@fyrirtaeki.is" data-testid="email-from-input" />
                </div>
                {email.method === 'smtp' ? (
                  <>
                    <div className="space-y-2">
                      <Label>M365 netfang (notandanafn)</Label>
                      <Input value={email.smtp_username} onChange={(e) => setEmail({ ...email, smtp_username: e.target.value })}
                        placeholder="netfang@fyrirtaeki.is" data-testid="email-smtp-user-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Lykilorð</Label>
                      <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)}
                        placeholder={email.has_smtp_password ? '••••••••  (vistað)' : 'Lykilorð pósthólfsins'} data-testid="email-smtp-pass-input" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Tenant ID</Label>
                      <Input value={email.graph_tenant_id} onChange={(e) => setEmail({ ...email, graph_tenant_id: e.target.value })} data-testid="email-tenant-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input value={email.graph_client_id} onChange={(e) => setEmail({ ...email, graph_client_id: e.target.value })} data-testid="email-client-input" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Client Secret</Label>
                      <Input type="password" value={graphSecret} onChange={(e) => setGraphSecret(e.target.value)}
                        placeholder={email.has_graph_secret ? '••••••••  (vistað)' : 'Client secret úr Azure'} data-testid="email-secret-input" />
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button type="submit" disabled={emailSaving} data-testid="email-save-btn">
                  <Save className="mr-2 h-4 w-4" />
                  {emailSaving ? 'Vistar...' : 'Vista'}
                </Button>
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="netfang til að prófa"
                  className="w-56" data-testid="email-test-to-input" />
                <Button type="button" variant="outline" onClick={handleEmailTest} disabled={testingEmail} data-testid="email-test-btn">
                  {testingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  {testingEmail ? 'Sendi...' : 'Senda prófunarpóst'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
